import { faExclamationTriangle, faRobot } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import AutomatedInsuranceOptionsForm, {
  type AutomatedInsuranceOptions
} from '@/components/AutomatedInsuranceOptionsForm';
import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import useAutoReverifyInsurancePolicy from '@/hooks/useAutoReverifyInsurancePolicy';
import usePerson from '@/hooks/usePerson';
import { ExternalVerificationSource } from '@/types/externalVerification';
import { Banner } from '@/ui';
import Button from '@/ui/components/Button';
import Container from '@/ui/components/Container';
import Toggle from '@/ui/components/Form/Toggle';
import Heading from '@/ui/components/Heading';
import LoadingSkeleton from '@/ui/components/LoadingSkeleton';
import Spinner from '@/ui/components/Spinner';
import Tooltip from '@/ui/components/Tooltip/Tooltip';
import { hasPermission, PEOPLE_RUN_VOB } from '@/utils/userPermission';

const AutoReverifyInsurance = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { insuranceId } = useParams<{ insuranceId: string }>();
  const { permissions, addMessage } = useContext(AppContext);

  const pathParts = location.pathname.split('/');
  // FIXME: This is probably dumb...
  const personIdIndex = pathParts.findIndex((part) => part === 'people') + 1;
  const personId = pathParts[personIdIndex];
  const insurancePolicy = location.state?.insurancePolicy;
  const { usePersonQuery } = usePerson({ id: personId });
  const { data: personData, isLoading: isPersonLoading } = usePersonQuery;

  const redirectPath = `/people/${personId}/insurance`;

  const [options, setOptions] = useState<AutomatedInsuranceOptions>({
    vendor: ExternalVerificationSource.FRONTRUNNER,
    serviceCode: '30',
    includeSsn: true
  });

  const { mutateAsync: reverifyPolicy, isPending: isSubmitting } = useAutoReverifyInsurancePolicy({
    onSuccess: () => {
      navigate(redirectPath);
    }
  });

  const canRunVob = useMemo(() => {
    const requiredPermission = PEOPLE_RUN_VOB;
    return hasPermission({ permissions, requiredPermission });
  }, [permissions]);

  useEffect(() => {
    if (!canRunVob) {
      navigate(`/people/${personId}/insurance`);
    }
  }, [canRunVob, navigate, personId]);

  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!insuranceId) {
      return;
    }

    try {
      await reverifyPolicy({
        policyId: insuranceId,
        data: {
          flags: {
            include_ssn: options.includeSsn
          },
          vendor: options.vendor,
          service_code: options.serviceCode
        }
      });
      navigate(redirectPath);
    } catch (error) {
      addMessage({
        level: 'error',
        text: `Failed to submit VOB request. ${error}`
      });
      navigate(redirectPath);
    }
  };

  const handleCancel = () => {
    navigate(redirectPath);
  };

  if (isPersonLoading) {
    return (
      <Container>
        <LoadingSkeleton />
      </Container>
    );
  }

  if (!canRunVob) {
    addMessage({
      level: 'warning',
      text: `Insufficient permissions`
    });
    navigate(redirectPath);
  }

  const policyName = insurancePolicy?.carrier || 'Unknown Policy';
  const personName = personData?.data
    ? `${personData.data.first_name} ${personData.data.last_name}`
    : 'Unknown Person';

  return (
    <Container>
      <div className="max-w-lg mx-auto py-6 px-2">
        {/* Main Header */}
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-2 text-center">
          <div className="mb-2">
            <FontAwesomeIcon icon={faRobot} className="text-gray-400 mr-3 text-2xl" />
            <Heading value={2} text="Automatic Reverification" />
          </div>
          <Heading
            value={3}
            text={`Reverify ${policyName} for member ${personName}`}
            className="text-blue-900"
          />
        </div>

        {/* Warning Banner */}
        <div className="mb-4">
          <Banner
            label="Running a verification will generate both an External Policy Verification and an Insurance Verification record for this policy. Note, if you use Stedi, it will only gather response data from them."
            color="yellow"
            icon={faExclamationTriangle}
          />
        </div>

        <form className="flex flex-col gap-4" onSubmit={handleFormSubmit}>
          <div className="bg-white p-6 rounded-lg shadow-sm border space-y-6">
            <AutomatedInsuranceOptionsForm value={options} onChange={setOptions} />

            {/* Run Discovery Toggle */}
            <div className="opacity-70">
              <Toggle
                enabled={false}
                onChange={() => {}} // * NOTE: No-op since it's disabled
                label="Run discovery processes for coverage and eligibility"
                disableInput={true}
              />
            </div>
          </div>

          {isSubmitting ? (
            <div className="flex flex-col gap-2 my-4">
              <Spinner label="Processing VOB..." />
            </div>
          ) : (
            <div className="flex gap-4 mt-2">
              <Tooltip
                tooltipContent="Jobs can take up to 5 minutes to complete"
                arrow={false}
                className="bg-yellow-100 border border-yellow-300 text-yellow-800">
                <div className="flex-1">
                  <Button
                    ariaLabel="Run VOB"
                    color={Color.EMERALD}
                    size="sm"
                    className="w-full"
                    type="submit"
                    disabled={!canRunVob}>
                    Run VOB
                  </Button>
                </div>
              </Tooltip>
              <Button
                ariaLabel="Cancel"
                onClick={handleCancel}
                color={Color.WHITE}
                size="sm"
                className="flex-1"
                type="button">
                Cancel
              </Button>
            </div>
          )}
        </form>
      </div>
    </Container>
  );
};

export default AutoReverifyInsurance;
